import "./MapSideModalContent.scss"
import { useEffect, useState } from "react"
import { IEnumConfig, IMapData, IMapLayer, IMapLayerDetails } from "../../../../shared/models/map_models"
import { Chip, DialogTitle, Divider, Popover, Tooltip, Typography, useTheme } from "@mui/material"
import MapLayerIcon from "../MapLayerIcon/MapLayerIcon"
import { IEnhancedLayerDetailsData } from "../../map-models"
import dayjs from "dayjs";
import * as mapUtils from '../../map-utils'

interface IMapSideModalContentProps {
    layerDetails: IMapLayerDetails,
    mapPoint: IMapData,
    mapLayer: IMapLayer
}


const MapSideModalContent = (props: IMapSideModalContentProps) => {

    const theme = useTheme()

    const [dataToDisplay, setDataToDisplay] = useState<any>("")
    const [label, setLabel] = useState<string>("")
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement  | null>(null)
    const [popoverText, setPopoverText] = useState<string | null>(null)
    const [keyValueData, setKeyValueData] = useState<Array<IEnhancedLayerDetailsData>>([])

    useEffect(() => {
        let dataValue: any = null
        if(props.layerDetails?.key)
            dataValue = mapUtils.getObjectValueFromKey(props.layerDetails.key, props.mapPoint)

        if (props.layerDetails.label)
            setLabel(props.layerDetails.label)

        if (Array.isArray(dataValue)){
            // For now we have this weird check for names array, in future data should be refactored to not have this be in an array
            if (typeof dataValue[0] !== "string" && dataValue[0]){
                setDataToDisplay(dataValue[0].name)
            } else {
                setDataToDisplay(dataValue)
            }
        } else {
            if(props.layerDetails.type === 'address'){
                const isEmpty = mapUtils.isAddressEmpty(dataValue)
                if(isEmpty){
                    dataValue = null
                }
            }
            setDataToDisplay(dataValue)
        }
    }, [])

    useEffect(() => {

        if (props.layerDetails.type === 'key_value'){
            const enhancedDetails: Array<IEnhancedLayerDetailsData> = []
            props.layerDetails.data?.forEach((detail) => {
                const displayValue = mapUtils.getDisplayDataForKeyValue(detail, props.mapPoint)
                if (displayValue !== null && displayValue !== undefined && displayValue !== ''){
                    const enhancedDetal = {
                        ...detail,
                        displayValue
                    }

                    if (detail.type === 'datetime')
                        enhancedDetal.displayValue = dayjs(displayValue).format("MM/DD/YYYY");

                    if (detail.display === 'list'){
                        const splitValue = displayValue.split(",")
                        enhancedDetal.tooltip = displayValue
                        if(splitValue.length > 8){
                            enhancedDetal.displayValue = splitValue.slice(0, 8).join(",") + `...`
                        }
                    } 

                    if (detail.type === 'object_list'){
                        const tempDisplayValue: any = []
                
                        displayValue.forEach((val: Object) => {
                            const tempStrings: any = []
                            Object.entries(val).map(([key, value]) => {
                                if(!detail.allowed_keys || detail.allowed_keys.includes(key)){
                                    tempStrings.push(`${key.toUpperCase()}: ${value}`)
                                }
                            })

                            tempDisplayValue.push(tempStrings)
                        })
                        
                        if (tempDisplayValue.length > 0)
                            enhancedDetal.displayValue = tempDisplayValue
                        else
                            enhancedDetal.displayValue = null

                    }

                    if (enhancedDetal.displayValue !== null)
                        enhancedDetails.push(enhancedDetal)
                }
            })
            setKeyValueData(enhancedDetails)
        }
    }, [props.layerDetails])

    const openPopover = (event: React.MouseEvent<any>, popoverData: any) => {
        setAnchorEl(event.currentTarget)
        if (typeof popoverData === 'string')
            setPopoverText(popoverData)
    }

    const closePopOver = () => {
        setAnchorEl(null)
    }

    const getEnumColor = (value: string | boolean, enumConfigs: Array<IEnumConfig>): IEnumConfig => {
        const config = enumConfigs.find((x) => {
            if (typeof value === 'boolean' && x.value === value)
                return x
            else if (typeof value === 'string' && typeof x.value === 'string' && x.value.toLowerCase() === value.toLowerCase())
                return x
                
        })

        if(config){
            return config
        } else {
            return {value: "", bg_color: 'transparent', text_color: theme.palette.text.primary}
        }
    }


    const getEnumDisplay = (displayValue: string | boolean, enumConfigs: Array<IEnumConfig>) => {
        return (
            <span
                className="enum-content"
                style={{
                    backgroundColor: getEnumColor(displayValue,enumConfigs).bg_color,
                    color: getEnumColor(displayValue, enumConfigs).text_color
                }}
            >{typeof displayValue === 'string' ? displayValue : displayValue.toString().toUpperCase()}</span>
        )
    }

    return(
        <>
            {props.layerDetails.type === 'title' && typeof dataToDisplay === 'string' &&
                <DialogTitle className="card-details-title">
                    <MapLayerIcon mapLayer={props.mapLayer}/>
                    <span>{dataToDisplay}</span>
                </DialogTitle>
            }
            {props.layerDetails.type === 'address' && dataToDisplay !== null &&
                <div className="side-modal-content">
                    <span className="modal-sub-title">{label}</span>
                    {dataToDisplay?.venue && <span className="light-content">{dataToDisplay.venue}</span>}
                    <span className="light-content">{dataToDisplay.line_1}</span>
                    <span className="light-content">{dataToDisplay.line_2}</span>
                    <span className="light-content">{dataToDisplay.city}, {dataToDisplay.state}</span>
                    <span className="light-content">{dataToDisplay.zip}</span>
                </div>
            }
            {props.layerDetails.type === 'chip' && dataToDisplay && dataToDisplay.length > 0 &&
                <div className="side-modal-content">
                    <span className="modal-sub-title">{label}</span>
                    <div className="chip-content-wrapper">
                        {dataToDisplay.map((data: any, i: number) => {
                            return (
                                <Chip 
                                    label={data} 
                                    className="chip-modal-content" 
                                    key={i}
                                    onClick={(e) => openPopover(e, data)}
                                />
                            )
                        })}
                        <Popover
                            open={!!anchorEl}
                            anchorEl={anchorEl}
                            onClose={closePopOver}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'left',
                            }}
                        >
                            <Typography sx={{ p: 1 }}>{popoverText}</Typography>
                        </Popover>
                    </div>
                </div>
            }
            {props.layerDetails.type === 'blurred' &&
                <div className="side-modal-content">
                    <span className="modal-sub-title">{label}</span>
                    {props.layerDetails.data?.map((detail, i) => (
                        <div key={i} className="light-content" style={i !== 0 ? {marginTop: "10px"} : {}}>
                            <Tooltip title={"Restricted content. Subscribe to a premium plan to view."}>
                                <div>
                                    <span className="blurred-label">{detail.label}:</span>
                                    <span className="blurred">{detail.key}</span>
                                </div>
                            </Tooltip>
                        </div>
                    ))}
                </div>
            }
            {props.layerDetails.type === 'key_value' && keyValueData.length > 0 &&
                <div className="side-modal-content">
                    <span className="modal-sub-title">{label}</span>
                    {keyValueData.map((detail, i ) => {
                        return (
                            !!detail.displayValue ? 
                                <div key={i} className="list-content" onClick={() => console.log(detail)} style={i !== 0 ? {margin: "5px 0px"} : {}}>
                                    {!!detail.label && !!detail.displayValue && <span>{detail.label}: </span>}
                                    {(() => {
                                        switch(detail.type) {
                                            case 'link':
                                                return (
                                                    <a href={detail.displayValue} target="self">{detail.anchor_text || detail.displayValue}</a>
                                                )
                                            case 'enum':
                                                return (
                                                    <>
                                                        {getEnumDisplay(detail.displayValue, detail?.enum_configs || [])}
                                                    </>
                                                )
                                            case 'object_list':
                                                return (
                                                    <Tooltip title={detail?.tooltip || ""}>
                                                        {detail.displayValue.map((objectsList: Array<string>) => (
                                                            <>
                                                                <Divider sx={{backgroundColor: (theme) => theme.palette.text.primary}}/>
                                                                {objectsList.map((displayString: string) => (
                                                                    <div className="object-list-value">
                                                                        {displayString}
                                                                    </div>
                                                                ))}
                                                            </>
                                                        ))
                                                            
                                                        }
                                                    </Tooltip>
                                                )
                                            default:
                                                return (
                                                    <Tooltip title={detail.tooltip || detail.displayValue}>
                                                        {detail.displayValue}
                                                    </Tooltip>
                                                )
                                        }
                                    })()}
                                   
                                </div>
                            :
                            <></>
                        )
                    })}
                </div>
            }
            {props.layerDetails.type === 'enum' && dataToDisplay &&
                <div className="side-modal-content">
                    <span className="modal-sub-title">{label}</span>
                    <div>
                        {getEnumDisplay(dataToDisplay, props.layerDetails?.enum_configs || [])}
                    </div>
                </div>
            }
        </>
    )
}

export default MapSideModalContent